import type { ScaleLinear, ScaleSymLog, ScaleTime } from 'd3-scale';

import { TimeseriesUnit } from '../../../../core/types/timeseries.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import type { TimeseriesBandDataPoint } from '../../../types/timeseries-band.js';
import type {
  WeakMapDatapoints,
  SeriesMetadata,
} from '../../../types/timeseries.js';
import { getDatapointTime } from '../../../utils/timeseries.js';

/**
 * Return boolean related if data-point is gap
 *
 * @param dataPoint - The data points
 * @param datapointsMap -
 * @returns Whether the accessor is defined
 */
export const definedAccessor = (
  dataPoint: TimeseriesBandDataPoint,
  datapointsMap: WeakMapDatapoints,
) => !safeGet(datapointsMap, dataPoint).isGap;

/**
 * Get x-accessor base on xScale
 *
 * @param xScale - The x-axis scale
 * @param datapointsMap -
 * @returns The band chart x-accessor
 */
export const xAxisAccessor =
  (xScale: ScaleTime<number, number>, datapointsMap: WeakMapDatapoints) =>
  (dataPoint: TimeseriesBandDataPoint) => {
    const { start, end, center } = safeGet(datapointsMap, dataPoint);

    return xScale(getDatapointTime(start, end, center)) ?? 0;
  };

/**
 * Get y0-accessor base on yScale
 *
 * @param scale - The y axis scale
 * @param dataPointsMap - Data points metadata map
 * @returns The band chart y0-accessor
 */
export const y0AxisAccessor =
  (
    scale: ScaleSymLog<number, number> | ScaleLinear<number, number>,
    dataPointsMap: WeakMapDatapoints,
  ) =>
  (dataPoint: TimeseriesBandDataPoint) => {
    const dataPointMetadata = safeGet(dataPointsMap, dataPoint);
    const value = dataPointMetadata.y0 ?? 0;

    return scale(value);
  };

/**
 * Get y1-accessor base on yScale
 *
 * @param scale - The y-axis scale
 * @param dataPointsMap - Data points metadata map
 * @returns The band chart y1-accessor
 */
export const y1AxisAccessor =
  (
    scale: ScaleSymLog<number, number> | ScaleLinear<number, number>,
    dataPointsMap: WeakMapDatapoints,
  ) =>
  (dataPoint: TimeseriesBandDataPoint) => {
    const dataPointMetadata = safeGet(dataPointsMap, dataPoint);
    const value = dataPointMetadata.y1 ?? 0;

    return scale(value);
  };

/**
 * Get curry accessor of the y1-accessor
 *
 * @returns y1AxisAccessor
 */
export function y1(
  yScaleByUnit: (
    unit: TimeseriesUnit,
  ) => ScaleSymLog<number, number> | ScaleLinear<number, number>,
  seriesMetadata: SeriesMetadata,
  datapointsMap: WeakMapDatapoints,
) {
  return (dataPoint: TimeseriesBandDataPoint) =>
    y1AxisAccessor(
      yScaleByUnit(seriesMetadata.unit),
      datapointsMap,
    )(dataPoint) ?? 0;
}

/**
 * Get curry accesor of the y0-accessor
 *
 * @returns y0AxisAccessor
 */
export function y0(
  yScaleByUnit: (
    unit: TimeseriesUnit,
  ) => ScaleSymLog<number, number> | ScaleLinear<number, number>,
  seriesMetadata: SeriesMetadata,
  datapointsMap: WeakMapDatapoints,
) {
  return (dataPoint: TimeseriesBandDataPoint) =>
    y0AxisAccessor(
      yScaleByUnit(seriesMetadata.unit),
      datapointsMap,
    )(dataPoint) ?? 0;
}
